<script setup lang="ts">
import { ref, computed } from 'vue'
import Layout from './components/Layout.vue'
import Home from './views/Home.vue'
import Notes from './views/Notes.vue'
import Files from './views/Files.vue'
import Summary from './views/Summary.vue'
import Tasks from './views/Tasks.vue'
import Plans from './views/Plans.vue'
import StartupItem from './views/StartupItem.vue'

// 简单的路由实现 - Hash 模式
const currentPath = ref(window.location.hash.slice(1) || '/')

const navigate = (path: string) => {
  currentPath.value = path
  window.location.hash = path
}

// 监听 hash 变化
window.addEventListener('hashchange', () => {
  currentPath.value = window.location.hash.slice(1) || '/'
})

const isHome = computed(() => currentPath.value === '/' || currentPath.value === '')
const isNotes = computed(() => currentPath.value === '/notes')
const isFiles = computed(() => currentPath.value === '/files')
const isSummary = computed(() => currentPath.value === '/summary')
const isTasks = computed(() => currentPath.value === '/tasks')
const isPlans = computed(() => currentPath.value === '/plans')
const isStartupItem = computed(() => currentPath.value.startsWith('/startup/item'))

// 获取项目详情页ID (hash 模式下从 hash 后的查询参数获取)
const startupItemId = computed(() => {
  const hash = window.location.hash
  const queryIndex = hash.indexOf('?')
  if (queryIndex === -1) return ''
  const queryString = hash.slice(queryIndex + 1)
  const params = new URLSearchParams(queryString)
  return params.get('id') || ''
})

const activeTab = ref('0')

const tabs = [
  { key: '1', label: 'MVP' },
  { key: '0', label: 'PERSEVERE' },
  { key: '4', label: 'PIVOT' },
]

const homeRef = ref<InstanceType<typeof Home> | null>(null)

const handleAddProject = () => {
  // 调用 Home 组件的 openAddDrawer 方法
  homeRef.value?.openAddDrawer()
}

const goBack = () => {
  window.history.back()
}
</script>

<template>
  <Layout 
      :show-add="isHome" 
      :show-back="isStartupItem"
      @add="handleAddProject" 
      @navigate="navigate"
      @back="goBack"
    >
    <template #header>
      <div class="header-content">
        <div v-if="isHome" class="tabs">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            class="tab-btn"
            :class="{ active: activeTab === tab.key }"
            @click="activeTab = tab.key"
          >
            {{ tab.label }}
          </button>
        </div>
        <div v-else-if="isNotes" class="page-title">
          <h2>创业笔记</h2>
        </div>
        <div v-else-if="isFiles" class="page-title">
          <h2>文件管理</h2>
        </div>
        <div v-else-if="isSummary" class="page-title">
          <h2>每日总结</h2>
        </div>
        <div v-else-if="isTasks" class="page-title">
          <h2>任务管理</h2>
        </div>
        <div v-else-if="isPlans" class="page-title">
          <h2>计划管理</h2>
        </div>
      </div>
    </template>

    <template #default>
      <Home v-if="isHome" ref="homeRef" :active-tab="activeTab" />
      <Notes v-else-if="isNotes" />
      <Tasks v-else-if="isTasks" />
      <Plans v-else-if="isPlans" />
      <Files v-else-if="isFiles" />
      <Summary v-else-if="isSummary" />
      <StartupItem v-else-if="isStartupItem" :id="startupItemId" />
      <div v-else class="not-found">
        <h2>页面不存在</h2>
        <button @click="navigate('/')">返回首页</button>
      </div>
    </template>
  </Layout>
</template>

<style scoped>
.header-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  width: 100%;
}

.header-content h1 {
  margin: 0;
  font-size: 1.5rem;
}

.tabs {
  display: flex;
  gap: 0;
  height: 60px;
  align-items: center;
}

.tab-btn {
  padding: 0 1.5rem;
  border: none;
  background: transparent;
  color: #666;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  height: 100%;
  display: flex;
  align-items: center;
  position: relative;
}

.tab-btn:hover {
  color: #333;
}

.tab-btn.active {
  color: #42b883;
  font-weight: 600;
}

.tab-btn.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 20%;
  right: 20%;
  height: 3px;
  background: #42b883;
  border-radius: 2px 2px 0 0;
}

.page-title h2 {
  margin: 0;
  font-size: 1.25rem;
  color: #333;
  font-weight: 600;
}

.not-found {
  padding: 3rem;
  text-align: center;
}

.not-found h2 {
  color: #666;
  margin-bottom: 1rem;
}

.not-found button {
  padding: 0.5rem 1.5rem;
  background: #42b883;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.category-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
  overflow-x: auto;
}

.category-btn {
  padding: 0.4rem 1rem;
  border: 1px solid #e0e0e0;
  background: #fff;
  color: #666;
  font-size: 0.85rem;
  cursor: pointer;
  border-radius: 16px;
  transition: all 0.2s;
  white-space: nowrap;
}

.category-btn:hover {
  border-color: #42b883;
  color: #42b883;
}

.category-btn.active {
  background: #42b883;
  border-color: #42b883;
  color: #fff;
}

.welcome {
  padding: 2rem;
  text-align: center;
}

.welcome h2 {
  color: #42b883;
  margin-bottom: 1rem;
}

.welcome p {
  color: #666;
  font-size: 1.1rem;
}
</style>
